import { useTranslation } from 'react-i18next';
import CatalogListHeader from '../../../components/CatalogListHeader/CatalogListHeader';
import { ActionIcon, Grid, Tooltip } from '@mantine/core';
import { useLocation } from 'react-router-dom';
import CatalogContainer from '../../../components/CatalogContainer/CatalogContainer';
import useDownloadExcel from '../../../hooks/useDownloadExcel/useDownloadExcel';
import { DatePickerInput, DateValue } from '@mantine/dates';
import { AreaChart } from '@mantine/charts';
import { useMemo, useState } from 'react';
import { useGetSalesdReport } from '../../../hooks/reports/queries';
import { exportSalesReport } from '../../../services/reports.service';
import { IconFileSpreadsheet } from '@tabler/icons-react';
import TableView from './TableView';
import { MRT_PaginationState } from 'mantine-react-table';
import { getInitialReportRange, getRangeDate, getReportExclusiveEnd, getReportTimezone } from '../datePickerHelper';

const DailySalesReport = () => {
  const salesReportType = 'daily';
  const { t } = useTranslation(['ReportsPage', 'FieldName', 'CommonButtons', 'Notifications']);
  const location = useLocation();
  const { downloadExcelV2, loadingExport } = useDownloadExcel();
  const [dateRangePicker, setDateRange] = useState<[DateValue, DateValue]>(() => getInitialReportRange(location.search));
  const [appliedRange, setAppliedRange] = useState(dateRangePicker);
  const updateDateRange = (value: [DateValue, DateValue]) => {
    setDateRange(value);
    if (value[0] && value[1]) {
      setAppliedRange(value);
      setPagination((current) => ({ ...current, pageIndex: 0 }));
    }
  };

  const searchParams = new URLSearchParams(location.search);
  const searchParamStoreId = searchParams.get('storeId') || '';
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 9
  });
  const timezone = getReportTimezone(location.search);
  const { fromDate, toDate } = getRangeDate(appliedRange, 'day', timezone, getReportExclusiveEnd(location.search));
  const {
    data,
    isLoading: isGetLoadingOrders,
    isError
  } = useGetSalesdReport(searchParamStoreId, {
    fromDate,
    toDate,
    timezone: timezone,
    type: salesReportType
  });

  const salesData: {
    date: string;
    ordersQty: number;
    ordersUnpaidTotal: number;
    salesTotal: number;
    totalProductPieces: number;
  }[] = useMemo(() => {
    return data?.tableInfo;
  }, [data]);
  const totalRowsCount = useMemo(() => data?.totalRows, [data]);
  const pages = useMemo(() => Math.ceil(totalRowsCount / 9) || -1, [totalRowsCount]);

  return (
    <CatalogContainer>
      <CatalogListHeader title={t('ReportsPage:dailySalesReport.title')} showCreateButton={false} />
      <Grid>
        <Grid.Col span={{ xs: 12, md: 'auto' }}>
          <DatePickerInput
            clearable={false}
            type="range"
            label={t('FieldName:date')}
            placeholder={t('FieldName:date')}
            value={dateRangePicker}
            onChange={updateDateRange}
            allowSingleDateInRange
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, md: 'content' }} style={{ alignSelf: 'flex-end' }}>
          <Tooltip label={t('CommonButtons:exportList')}>
            <ActionIcon
              disabled={loadingExport}
              onClick={() => {
                if (!fromDate || !toDate) return;
                downloadExcelV2(async () => {
                  try {
                    return await exportSalesReport(searchParamStoreId, {
                      fromDate,
                      toDate,
                      timezone: timezone,
                      type: salesReportType
                    });
                  } catch (e) {
                    throw e;
                  }
                }, `Reporte_${searchParamStoreId}`);
              }}
              variant="filled"
              color="#1D6F42"
              aria-label="Descargar_Listado_CSV">
              <IconFileSpreadsheet style={{ width: '70%', height: '70%' }} stroke={1.5} />
            </ActionIcon>
          </Tooltip>
        </Grid.Col>

        <Grid.Col>
          <AreaChart
            h={400}
            data={Object.keys(data?.graphData || {})?.map((dayKey: string) => {
              const sale = data?.graphData[dayKey];
              return {
                salesDate: dayKey,
                totalSale: sale
              };
            })}
            dataKey="salesDate"
            series={[{ name: 'totalSale', color: 'indigo.6', label: t('FieldName:orders') }]}
            curveType="linear"
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <TableView
            salesData={salesData}
            totalRowsCount={totalRowsCount}
            pages={pages}
            isError={isError}
            isLoading={isGetLoadingOrders}
            pagination={pagination}
            footerData={data?.totalRowStats}
            setPagination={setPagination}
          />
        </Grid.Col>
      </Grid>
    </CatalogContainer>
  );
};

export default DailySalesReport;
